import Header from "../components/header/Header";
import { Table, Button, Input, Space, Spin } from "antd";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Highlighter from "react-highlight-words";
import { SearchOutlined } from "@ant-design/icons";
import { apiRequest } from "../utils/apiClient";

const withNA = (value) => {
  if (value === null || value === undefined || value === "") {
    return "NA";
  }
  return value;
};

const InvoicePage = () => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  useEffect(() => {
    const getInvoices = async () => {
      setLoading(true);
      try {
        const res = await apiRequest(
          "/api/invoices/get-all?page=1&limit=20",
          { method: "GET" }
        );

        const items = Array.isArray(res) ? res : res.data || [];
        const newData = items.map((item) => ({
          ...item,
          key: item._id,
        }));

        setInvoices(newData);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    getInvoices();
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={t("searchColumn", { column: dataIndex })}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys, confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t("search")}
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            {t("reset")}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            {t("filter")}
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            {t("close")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : false,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      const displayText = withNA(text);

      if (searchedColumn === dataIndex && text) {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        );
      }

      return displayText;
    },
  });

  const columns = [
    {
      title: t("customerName"),
      dataIndex: "customerName",
      key: "customerName",
      ...getColumnSearchProps("customerName"),
    },
    {
      title: t("phoneNumber"),
      dataIndex: "customerPhoneNumber",
      key: "customerPhoneNumber",
      ...getColumnSearchProps("customerPhoneNumber"),
    },
    {
      title: t("transactionDateAndTime"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => {
        if (!text) return "NA";

        const date = new Date(text);
        return date.toLocaleString("en-PK", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
  ];

  return (
    <>
      <Header />

      <div className="px-6 min-h-[550px]">
        <h1 className="text-2xl text-center font-semibold mb-4 text-gray-800">
          {tCommon("nav.customers")}
        </h1>

        {loading ? (
          <div className="flex justify-center items-center h-[500px]">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            size="medium"
            dataSource={invoices}
            columns={columns}
            bordered
            pagination={false}
            scroll={{ x: 1200, y: 500 }}
            rowKey="_id"
          />
        )}
      </div>
    </>
  );
};

export default InvoicePage;
